import type { FC } from 'react';

import {
  Divider,
  Flex,
  Header,
  PrimaryButton,
  Spacer,
  TertiaryButton,
  Text,
  Toolbar,
} from '@procurify/ui'
import { FormattedMessage } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import { ImporterErrorList } from './ImporterErrorList'
import { CommonImporterErrorStageStyled } from './styles/CommonImporterErrorStageStyled'
import messages from '../../messages'
import { ImporterContentStyled as Content } from '../../styles/ImporterContentStyled'

export interface CommonImporterError {
  csvLineIndex: number
  field: string
  message: string
  value: string
  errorCode: string
  errorKey: string
  isChecked?: boolean
}

export interface ICommonImporterErrorStageProps {
  errors: CommonImporterError[][]
  failedRowCount?: number
  title?: string
  onPreviousStep?: () => void
  onFirstStep?: () => void
  onDismiss?: () => void
}

export const CommonImporterErrorStage: FC<
  ICommonImporterErrorStageProps
> = ({
  errors,
  failedRowCount = 0,
  title = 'rows',
  onPreviousStep,
  onFirstStep,
  onDismiss,
}) => {
  const intl = useProcurifyIntl()

  return (
    <CommonImporterErrorStageStyled>
      <Content>
        <Header>
          <FormattedMessage
            values={{ failedRowCount }}
            {...messages.improvedErrorStepTitle}
          />
        </Header>

        <Spacer />

        <Text>
          {intl.formatMessage(messages.errorStepDescriptionOne)}
          <br />
          {intl.formatMessage(messages.errorStepDescriptionTwo)}
          <a
            target='_blank'
            rel='noopener noreferrer'
            href='https://success.procurify.com/hc/en-us/sections/1500000211482'
          >
            {intl.formatMessage(messages.errorGuideLink)}
          </a>
        </Text>

        <Spacer space={2} />

        <ImporterErrorList errors={errors} />

        <Spacer space={2} />

        <div className='fix-options-container'>
          <Spacer space={2} />

          <Header as='h4' gutter>
            {intl.formatMessage(messages.improvedErrorFixTitle)}
          </Header>

          <Flex.Row justifyContent='space-around' width={912}>
            <Flex.Item width={1 / 3}>
              <Header as='h5' gutter>
                {intl.formatMessage(messages.errorFixOptionOneTitle)}
              </Header>
              <Text>
                {intl.formatMessage(messages.errorFixOptionOneDescription)}
              </Text>

              <Spacer space={1} />

              <PrimaryButton onClick={onFirstStep}>
                {intl.formatMessage(messages.uploadAgainButton)}
              </PrimaryButton>
            </Flex.Item>

            <Flex.Item width={1 / 3}>
              <Header as='h5' gutter>
                {intl.formatMessage(messages.errorFixOptionTwoTitle)}
              </Header>
              <Text>
                {intl.formatMessage(messages.errorFixOptionTwoDescription)}
              </Text>

              <Spacer space={1} />

              <PrimaryButton onClick={onPreviousStep}>
                {intl.formatMessage(messages.remapColumnsButton)}
              </PrimaryButton>
            </Flex.Item>
          </Flex.Row>

          <Spacer space={2} />
        </div>

        <Divider space={2} />

        <Toolbar>
          <Toolbar.Fill />
          <Toolbar.Item>
            <TertiaryButton onClick={onDismiss}>
              <FormattedMessage {...messages.cancelImportButton} />
            </TertiaryButton>
          </Toolbar.Item>
        </Toolbar>
      </Content>
    </CommonImporterErrorStageStyled>
  )
}
